import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { prefetchRelatedRoutes } from '../../utils/routePrefetcher';

/**
 * Component that tracks route changes and prefetches related routes
 * This component doesn't render anything, it just performs side effects
 */
const RouteChangeTracker: React.FC = () => {
  const location = useLocation();

  // Keep track of the last path to avoid duplicate logging
  const lastPathRef = useRef<string>('');

  useEffect(() => {
    // Skip if this is the same path (prevents duplicate logs)
    if (lastPathRef.current === location.pathname) {
      return;
    }

    // Update the last path
    lastPathRef.current = location.pathname;

    // When the route changes, prefetch related routes
    prefetchRelatedRoutes(location.pathname);

    // Track page view for analytics
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'UA-XXXXXXXX-X', {
        page_path: location.pathname + location.search
      });
    }

    // Log performance metrics for the page navigation
    if (process.env.NODE_ENV === 'development' && window.performance && window.performance.getEntriesByType) {
      // Get navigation timing
      const navigationEntries = window.performance.getEntriesByType('navigation');
      if (navigationEntries.length > 0) {
        const navigationTiming = navigationEntries[0] as PerformanceNavigationTiming;

        // Calculate key metrics
        const ttfb = navigationTiming.responseStart - navigationTiming.requestStart;
        const domInteractive = navigationTiming.domInteractive - navigationTiming.startTime;
        const domComplete = navigationTiming.domComplete - navigationTiming.startTime;

        // Log the metrics (only in development)
        console.log(`Page Navigation Performance (${location.pathname}):`);
        console.log(`- Time to First Byte: ${ttfb.toFixed(2)}ms`);
        console.log(`- DOM Interactive: ${domInteractive.toFixed(2)}ms`);
        console.log(`- DOM Complete: ${domComplete.toFixed(2)}ms`);
      }
    }
  }, [location]);

  return null;
};

export default RouteChangeTracker;
